import type { ExternalToast } from "sonner"

import { password } from "../../config/constants"
import { toast } from "../../shared-components/ui/toast"
import { logError, logMessage } from "../../shared-components/utils"

type ConsoleLogLevel = "always" | "never" | "when-skipped"
type LogLevel = "debug" | "error" | "info" | "warning"

export const ALL_LOG_LEVELS: LogLevel[] = ["warning", "error", "info", "debug"]

const TOAST_LEVELS: LogLevel[] = ["error", "debug"]

const DEFAULT_TOAST_DURATION_MS = 800

export type LoggerInput =
  | (LoggerInputBase & { input: Error; level: "error" })
  | (LoggerInputBase & { input: string; level: "debug" })
  | (LoggerInputBase & { input: string; level: "info" })
  | (LoggerInputBase & { input: string; level: "warning" })

interface LoggerInputBase {
  /** Default true for `error` and `debug`; set false to skip the toast. */
  toast?: LoggerToastOption
}

type LoggerToastOption = boolean | Pick<ExternalToast, "duration">

class Logger {
  #consoleLogLevel: ConsoleLogLevel
  #logLevels: LogLevel[]
  constructor(input: { consoleLogLevel: ConsoleLogLevel; levels: LogLevel[] }) {
    this.#logLevels = input.levels
    this.#consoleLogLevel = input.consoleLogLevel
  }
  logDebug(input: string) {
    this.#consoleLog({ allowedToLog: true, level: "info", message: input })
    logMessage(input, "debug")
  }
  logError(input: Error) {
    const allowedToLog = this.#logLevels.includes("error")
    this.#consoleLog({
      allowedToLog,
      error: input,
      level: "error",
      message: input?.message ?? "unknown message of error",
    })
    if (allowedToLog) logError(input, "error")
  }
  logInfo(input: string) {
    const allowedToLog = this.#logLevels.includes("info")
    this.#consoleLog({ allowedToLog, level: "info", message: input })
    if (allowedToLog) logMessage(input, "info")
  }
  logWarning(input: string) {
    const allowedToLog = this.#logLevels.includes("warning")
    this.#consoleLog({ allowedToLog, level: "warning", message: input })
    if (allowedToLog) logMessage(input, "warning")
  }
  #consoleLog(input: {
    allowedToLog: boolean
    error?: Error
    level: LogLevel
    message: string
  }) {
    if (
      (this.#consoleLogLevel === "when-skipped" && !input.allowedToLog) ||
      this.#consoleLogLevel === "always" ||
      input.level === "debug"
    ) {
      console.log(input.message, input.level.toUpperCase())
      if (input.error) console.error(input.error)
    }
  }
}

function messageFromInput(input: LoggerInput): string {
  return input.level === "error" ? input.input.message : input.input
}

function shouldToast(input: LoggerInput): boolean {
  if (input.toast === false) return false
  if (input.toast !== undefined && input.toast !== true) return true
  return TOAST_LEVELS.includes(input.level)
}

function showToast(input: LoggerInput) {
  if (!shouldToast(input)) return

  const message = messageFromInput(input)
  const options = toastOptions(input)

  switch (input.level) {
    case "debug":
      toast.info(message, options)
      break
    case "error":
      toast.error(message, options)
      break
    default:
      break
  }
}

function toastOptions(input: LoggerInput): ExternalToast | undefined {
  if (
    input.toast === false ||
    input.toast === true ||
    input.toast === undefined
  ) {
    return { duration: DEFAULT_TOAST_DURATION_MS }
  }
  return input.toast
}

let loggerInstance: Logger | null = null

export const initLoggerFn = (input: {
  consoleLogLevel: ConsoleLogLevel
  levels: LogLevel[]
})=> {
  if (loggerInstance) return
  loggerInstance = new Logger(input)
}

export const initLogger = ()=> {
  if (!password) return
  initLoggerFn({
    consoleLogLevel: "always",
    levels: ["info", "warning", "error", "debug"],
  })
  // initLoggerFn({
  //   consoleLogLevel: "never",
  //   levels: ["info", "warning", "error", "debug"],
  // })
}

export function logger(input: LoggerInput) {
  showToast(input)

  if (!loggerInstance) return
  if (input.level === "info") loggerInstance.logInfo(input.input)
  else if (input.level === "warning") loggerInstance.logWarning(input.input)
  else if (input.level === "error") loggerInstance.logError(input.input)
  else if (input.level === "debug") loggerInstance.logDebug(input.input)
}
